import { PropTypes } from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import Modal from '../../../../../components/Modal';
import RemoveModal from '../../../../../components/RemoveModal';
import { MainContext } from '../../../../../context/MainContext';
import WithBackButton from '../../../../../layout/WithBackButton';
import {
  clearCreated,
  clearEdited,
  clearErrors,
  createDevice,
  deleteDevices,
  getDevices,
  updateDevice,
} from '../../../../../services/redux/hoodedControl/devices/actions';
import CreateDevice from '../Create';
import { COLUMNS_HEADERS } from './columns';
import styles from './index.module.scss';
import { NotificationsContext } from '../../../../../context/NotificationsContext';

const { actionsWrapper, availability } = styles;

const List = ({ getItems, deleteItems }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { setLoading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const { isEdited, errorMessages, isCreated } = useSelector(
    ({ hoodedControlDevices }) => hoodedControlDevices,
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deviceForEdit, setDeviceForEdit] = useState();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const onSingleDeleteButtonClick = (device) => {
    setSelectedRows([device]);
    setShowRemoveModal(true);
  };

  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    deleteItems(selectedRows);
    setSelectedRows([]);
  };

  const editDevice = (device) => {
    setLoading(true);
    dispatch(updateDevice(device));
  };

  const handleCreateDevice = (device) => {
    setLoading(true);
    dispatch(createDevice(device));
  };

  useEffect(() => {
    if (isEdited) {
      setLoading(false);
      setDeviceForEdit(undefined);
      setShowEditModal(false);
      dispatch(clearEdited());
    }
  }, [isEdited, setLoading, dispatch]);

  useEffect(() => {
    if (isCreated) {
      setLoading(false);
      setShowCreateModal(false);
      dispatch(clearCreated());
    }
  }, [isCreated, setLoading, dispatch]);

  useEffect(() => {
    dispatch(clearErrors());
  }, [dispatch, showEditModal]);

  useEffect(() => {
    if (errorMessages) {
      const { status } = errorMessages.response;
      pushNotification({
        message: intl.formatMessage({
          id: `${
            status === 403
              ? 'strength.hoodedcontrol.devices.delete.403.error'
              : 'messages.default'
          }`,
        }),
        type: 'error',
      });
    }
  }, [errorMessages, intl, pushNotification]);

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className={`row ${actionsWrapper}`}>
          <div className="col-sm-12">
            <AccionaButton
              id="createBtn"
              onClick={() => {
                setShowCreateModal(true);
              }}
              msg={intl.formatMessage({ id: 'button.add' })}
              classIcon="icon fas fa-plus"
              type="white"
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <AccionaTable
            columns={COLUMNS_HEADERS}
            getItems={getItems}
            entity="hoodedControlDevices"
            dataParseFunction={(data) =>
              data.map((d) => ({
                ...d,
                project: d.project
                  ? `${d.project.work_code} ${d.project.name}`
                  : '--',
                inUse: (
                  <div className={availability}>
                    {d.inUse ? (
                      <>
                        {intl.formatMessage({ id: 'no' })}{' '}
                        <i className="iconRed fas fa-times-circle fa-lg"></i>
                      </>
                    ) : (
                      <>
                        {intl.formatMessage({ id: 'yes' })}{' '}
                        <i className="iconGreen fas fa-check-circle fa-lg"></i>
                      </>
                    )}
                  </div>
                ),
                rawObject: d,
              }))
            }
            actions={(device) => (
              <>
                <AccionaTableButton
                  onClick={() => {
                    setShowEditModal(true);
                    setDeviceForEdit(device.rawObject);
                  }}
                  type="edit"
                  toolTip={intl.formatMessage({
                    id: 'editAction.devices',
                  })}
                />
                <AccionaTableButton
                  onClick={() => {
                    onSingleDeleteButtonClick(device.rawObject);
                  }}
                  type="delete"
                  toolTip={intl.formatMessage({
                    id: 'deleteAction.devices',
                  })}
                />
              </>
            )}
          />
        </div>
      </div>

      <RemoveModal
        entity="devices"
        selectedRows={selectedRows}
        msg={intl.formatMessage(
          { id: 'delete.devices' },
          { total: selectedRows.length },
        )}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
      {deviceForEdit && (
        <Modal
          title={intl
            .formatMessage({
              id: 'strength.hoodedcontrol.devices.list.modal.edit.title',
            })
            .toUpperCase()}
          open={showEditModal}
          onCancel={() => {
            setShowEditModal(false);
          }}
          closable
          width="50vw"
        >
          <CreateDevice
            initialData={deviceForEdit}
            onFinish={editDevice}
            onCancel={() => {
              setShowEditModal(false);
              setDeviceForEdit(undefined);
            }}
          />
        </Modal>
      )}
      <Modal
        title={intl
          .formatMessage({
            id: 'strength.thermalcontrol.devices.list.modal.create.title',
          })
          .toUpperCase()}
        open={showCreateModal}
        onCancel={() => {
          setShowCreateModal(false);
        }}
        closable
        width="50vw"
      >
        <CreateDevice
          onFinish={handleCreateDevice}
          onCancel={() => {
            setShowCreateModal(false);
          }}
        />
      </Modal>
    </>
  );
};

List.propTypes = {
  getItems: PropTypes.func.isRequired,
  deleteItems: PropTypes.func.isRequired,
};

export default connect(() => ({}), {
  getItems: getDevices,
  deleteItems: deleteDevices,
})(WithBackButton(List));
